import * as React from "react";
import Dialog from "@src/components/Dialog";
import { GetEventRecommendationListsQuery } from "@src/@types/api.d";
import { AnimatePresence, motion } from "framer-motion";
import {
  BedDouble,
  BookHeart,
  Car,
  ChevronDown,
  CookingPot,
  ExternalLink,
  Image as ImageIcon,
  MapPin,
  Phone,
  Video,
} from "lucide-react";

type Props = {
  eventId: string | number;
  recommendationData: GetEventRecommendationListsQuery;
};

type RouteRecommendationList = NonNullable<
  GetEventRecommendationListsQuery["getEventRecommendationLists"][number]
>;

type RouteRecommendationItem = NonNullable<
  RouteRecommendationList["items"][number]
>;

type RecommendationCategory =
  | "VENUE"
  | "CATERING"
  | "FOOD"
  | "ACCOMMODATION"
  | "TRANSPORTATION"
  | "OTHER"
  | string;

type RecommendationMediaType = "IMAGE" | "VIDEO" | string;

type RecommendationItem = {
  id: string;
  name: string;
  description?: string | null;
  contactInfo?: string | null;
  link?: string | null;
  mediaUrl?: string | null;
  mediaType?: RecommendationMediaType | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

type RecommendationList = {
  id: string;
  name: string;
  category?: RecommendationCategory | null;
  description?: string | null;
  items?: RecommendationItem[] | null;
  createdAt?: string | null;
  updatedAt?: string | null;
};

const CATEGORY_LABELS: Record<string, string> = {
  VENUE: "Venue",
  CATERING: "Catering",
  FOOD: "Food",
  ACCOMMODATION: "Accommodation",
  TRANSPORTATION: "Transportation",
  OTHER: "Other",
};

const CATEGORY_META: Record<
  string,
  { icon: React.ComponentType<{ className?: string }> }
> = {
  VENUE: { icon: MapPin },
  CATERING: { icon: CookingPot },
  FOOD: { icon: CookingPot },
  ACCOMMODATION: { icon: BedDouble },
  TRANSPORTATION: { icon: Car },
  OTHER: { icon: BookHeart },
};

const toTitleCase = (value?: string | null): string => {
  if (!value) {
    return "Untitled List";
  }

  return value
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getCategoryLabel = (category?: string | null): string => {
  if (!category) {
    return "Other";
  }

  return CATEGORY_LABELS[category] ?? toTitleCase(category.replace(/_/g, " "));
};

const getCategoryMeta = (category?: string | null) => {
  if (!category) {
    return CATEGORY_META.OTHER;
  }

  return CATEGORY_META[category] ?? CATEGORY_META.OTHER;
};

const MediaFallback: React.FC<{
  category?: RecommendationCategory | null;
  minHeightClassName?: string;
}> = ({ category, minHeightClassName = "min-h-[120px]" }) => {
  const categoryMeta = getCategoryMeta(category);
  const CategoryIcon = categoryMeta.icon;

  return (
    <div
      className={`w-full h-full ${minHeightClassName} bg-[#d9cfb3] border border-[#4B4838] rounded-lg flex items-center justify-center`}
      aria-label="No media available"
    >
      <div className="w-14 h-14 rounded-full flex items-center justify-center bg-[#EBE3CE] text-[#4B4838]">
        <CategoryIcon className="w-7 h-7" aria-hidden="true" />
      </div>
    </div>
  );
};

const ItemMedia: React.FC<{
  item: RecommendationItem;
  category?: RecommendationCategory | null;
  minHeightClassName?: string;
}> = ({ item, category, minHeightClassName = "min-h-[120px]" }) => {
  if (!item.mediaUrl) {
    return (
      <MediaFallback
        category={category}
        minHeightClassName={minHeightClassName}
      />
    );
  }

  if (item.mediaType === "VIDEO") {
    return (
      <div className="relative w-full">
        <video
          className={`w-full h-full ${minHeightClassName} object-cover rounded-lg border border-[#4B4838]`}
          controls
          preload="metadata"
          aria-label={`${item.name} video preview`}
        >
          <source src={item.mediaUrl} />
          Your browser does not support video preview.
        </video>
        <div className="absolute top-2 right-2 rounded-full bg-black/60 text-white p-1.5">
          <Video className="w-4 h-4" aria-hidden="true" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative w-full">
      <img
        src={item.mediaUrl}
        alt={`${item.name} preview`}
        className={`w-full h-full ${minHeightClassName} object-cover rounded-lg border border-[#4B4838]`}
      />
      <div className="absolute top-2 right-2 rounded-full bg-black/55 text-white p-1.5">
        <ImageIcon className="w-4 h-4" aria-hidden="true" />
      </div>
    </div>
  );
};

const Recommendations: React.FC<Props> = ({ eventId, recommendationData }) => {
  const [openListId, setOpenListId] = React.useState<string | null>(null);
  const [isItemModalOpen, setIsItemModalOpen] = React.useState(false);
  const [selectedItemData, setSelectedItemData] = React.useState<{
    item: RecommendationItem;
    category?: RecommendationCategory | null;
  } | null>(null);

  const recommendationLists = React.useMemo(
    (): RecommendationList[] =>
      (recommendationData?.getEventRecommendationLists ?? [])
        .filter((list): list is RouteRecommendationList => Boolean(list))
        .map((list) => ({
          id: String(list.id),
          name: list.name,
          category: list.category,
          description: list.description,
          items: (list.items ?? [])
            .filter((item): item is RouteRecommendationItem => Boolean(item))
            .map((item) => ({
              id: String(item.id),
              name: item.name,
              description: item.description,
              contactInfo: item.contactInfo,
              link: item.link,
              mediaUrl: item.mediaUrl,
              mediaType: item.mediaType,
              createdAt:
                typeof item.createdAt === "string"
                  ? item.createdAt
                  : String(item.createdAt),
              updatedAt:
                typeof item.updatedAt === "string"
                  ? item.updatedAt
                  : String(item.updatedAt),
            })),
          createdAt:
            typeof list.createdAt === "string"
              ? list.createdAt
              : String(list.createdAt),
          updatedAt:
            typeof list.updatedAt === "string"
              ? list.updatedAt
              : String(list.updatedAt),
        })),
    [recommendationData]
  );

  const toggleList = (id: string) => {
    setOpenListId((prev) => (prev === id ? null : id));
  };

  const openItemModal = (
    item: RecommendationItem,
    category?: RecommendationCategory | null
  ) => {
    setSelectedItemData({ item, category });
    setIsItemModalOpen(true);
  };

  if (!eventId || recommendationLists.length === 0) {
    return null;
  }

  return (
    <section className="w-full py-14 md:py-20 border-b border-[#4B4838] bg-[#EBE3CE]">
      <div className="max-w-6xl mx-auto px-4 md:px-8">
        <div className="mb-8 md:mb-10 text-center">
          <h2 className="text-[#4B4838] text-3xl md:text-4xl lg:text-5xl font-normal leading-tight tracking-wide font-calabassas">
            Recommendations
          </h2>
          <p className="text-sm md:text-base text-[#4B4838] mt-3 font-inter opacity-90">
            Helpful places and services curated by the couple.
          </p>
        </div>

        <div className="space-y-4">
          {recommendationLists.map((list) => {
            const panelId = `recommendation-panel-${list.id}`;
            const isOpen = openListId === list.id;
            const items = list.items ?? [];
            const categoryMeta = getCategoryMeta(list.category);
            const CategoryIcon = categoryMeta.icon;

            return (
              <article
                key={list.id}
                className="rounded-2xl border border-[#7E6F5D] shadow-sm overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => toggleList(list.id)}
                  className="w-full p-5 md:p-6 text-left hover:bg-[#efe5cf] transition-colors"
                  aria-expanded={isOpen}
                  aria-controls={panelId}
                  aria-label={
                    isOpen
                      ? `Collapse ${toTitleCase(list.name)} details`
                      : `Expand ${toTitleCase(list.name)} details`
                  }
                >
                  <div className="grid grid-cols-[44px_minmax(0,1fr)_36px] md:grid-cols-[52px_minmax(0,1fr)_40px] items-center gap-3 md:gap-4">
                    <div className="flex items-center justify-center">
                      <div className="w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center bg-[#d9cfb3] text-[#4B4838]">
                        <CategoryIcon className="w-5 h-5" aria-hidden="true" />
                      </div>
                    </div>

                    <div className="min-w-0">
                      <div>
                        <div className="flex flex-wrap items-center gap-2 md:gap-3">
                          <h3 className="text-lg md:text-xl font-semibold text-[#4B4838] font-neue uppercase">
                            {toTitleCase(list.name)}
                          </h3>
                          {/* <span className="inline-flex text-xs font-medium rounded-full bg-[#fff8e6] text-[#8a6a1f] px-2.5 py-1 border border-[#f1e2b3]">
                              {getCategoryLabel(list.category)}
                            </span> */}
                        </div>
                        {list.description?.trim() && (
                          <p className="mt-2 text-sm md:text-base text-[#4B4838] font-inter opacity-90">
                            {list.description}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-center">
                      <ChevronDown
                        className={`w-5 h-5 text-[#4B4838] transition-transform duration-300 ${
                          isOpen ? "rotate-180" : ""
                        }`}
                        aria-hidden="true"
                      />
                    </div>
                  </div>
                </button>

                <AnimatePresence initial={false}>
                  {isOpen && (
                    <motion.div
                      id={panelId}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="border-t border-[#7E6F5D] p-5 md:p-6 bg-[#d9cfb3]">
                        {items.length === 0 ? (
                          <p className="text-sm text-[#4B4838] font-inter opacity-80">
                            No items in this list yet.
                          </p>
                        ) : (
                          <ul className="space-y-4">
                            {items.map((item) => (
                              <li
                                key={item.id}
                                className="rounded-xl border border-[#7E6F5D] bg-[#EBE3CE]"
                              >
                                <div
                                  role="button"
                                  tabIndex={0}
                                  onClick={() =>
                                    openItemModal(item, list.category)
                                  }
                                  onKeyDown={(event) => {
                                    if (
                                      event.key === "Enter" ||
                                      event.key === " "
                                    ) {
                                      event.preventDefault();
                                      openItemModal(item, list.category);
                                    }
                                  }}
                                  className="p-4 cursor-pointer transition-colors"
                                  aria-label={`Open ${item.name} details`}
                                >
                                  <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] md:items-center gap-4 md:gap-6">
                                    <div className="flex min-w-0 flex-col justify-center">
                                      <h4 className="text-base md:text-lg font-semibold text-[#4B4838] font-neue uppercase">
                                        {item.name}
                                      </h4>

                                      <div className="mt-3 flex flex-col items-start gap-2 text-sm text-[#4B4838] font-inter">
                                        {(item.link || item.contactInfo) && (
                                          <div className="flex w-full flex-wrap items-center gap-x-4 gap-y-2">
                                            {item.link && (
                                              <a
                                                href={item.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="inline-flex min-w-0 max-w-full md:max-w-[65%] items-center gap-1.5 text-[#4B4838] hover:underline"
                                                onClick={(event) =>
                                                  event.stopPropagation()
                                                }
                                              >
                                                <ExternalLink
                                                  className="w-4 h-4 flex-shrink-0"
                                                  aria-hidden="true"
                                                />
                                                <span className="truncate ">
                                                  {item.link}
                                                </span>
                                              </a>
                                            )}

                                            {item.contactInfo && (
                                              <span className="inline-flex items-center gap-1.5 text-[#4B4838]">
                                                <Phone
                                                  className="w-4 h-4 text-[#4B4838]"
                                                  aria-hidden="true"
                                                />
                                                {item.contactInfo}
                                              </span>
                                            )}
                                          </div>
                                        )}

                                        {!item.link && !item.contactInfo && (
                                          <span className="text-[#4B4838] opacity-70">
                                            {/* No contact or link provided. */}
                                          </span>
                                        )}

                                        {item.description?.trim() && (
                                          <p className="block w-full min-w-0 truncate text-[#4B4838] opacity-90 font-inter">
                                            {item.description}
                                          </p>
                                        )}
                                      </div>
                                    </div>

                                    <div>
                                      <ItemMedia
                                        item={item}
                                        category={list.category}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </article>
            );
          })}
        </div>
      </div>

      <Dialog
        open={isItemModalOpen}
        onOpenChange={setIsItemModalOpen}
        preventCloseAutoFocus
        trigger={<span className="hidden" aria-hidden="true" />}
      >
        {selectedItemData && (
          <div className="w-full max-w-[740px] lg:w-[40vw] lg:max-w-[40vw] mx-auto bg-[#F7F1E3] p-5 md:p-6 rounded-none md:rounded-2xl border-0 md:border md:border-[#7E6F5D] shadow-none md:shadow-xl">
            <div className="flex flex-col gap-4 md:gap-5">
              <div className="w-full">
                <ItemMedia
                  item={selectedItemData.item}
                  category={selectedItemData.category}
                  minHeightClassName="min-h-[160px] lg:min-h-[260px]"
                />
              </div>

              <h3 className="text-xl md:text-2xl font-semibold text-[#4B4838] leading-snug font-neue uppercase">
                {selectedItemData.item.name}
              </h3>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm md:text-base text-[#4B4838] font-inter">
                {selectedItemData.item.link ? (
                  <a
                    href={selectedItemData.item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-[#4B4838] hover:underline break-all"
                  >
                    <ExternalLink
                      className="w-4 h-4 md:w-5 md:h-5 flex-shrink-0"
                      aria-hidden="true"
                    />
                    {selectedItemData.item.link}
                  </a>
                ) : (
                  <div
                    className="min-h-[24px] min-w-[140px]"
                    aria-hidden="true"
                  />
                )}

                {selectedItemData.item.contactInfo ? (
                  <p className="inline-flex items-center gap-2">
                    <Phone
                      className="w-4 h-4 text-[#4B4838]"
                      aria-hidden="true"
                    />
                    {selectedItemData.item.contactInfo}
                  </p>
                ) : (
                  <div
                    className="min-h-[24px] min-w-[140px]"
                    aria-hidden="true"
                  />
                )}
              </div>

              <p className="text-sm md:text-base text-[#4B4838] leading-relaxed font-inter opacity-90">
                {selectedItemData.item.description?.trim() || (
                  <span className="block min-h-[24px]" aria-hidden="true" />
                )}
              </p>
            </div>
          </div>
        )}
      </Dialog>
    </section>
  );
};

export default Recommendations;
